import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Crown, Sparkles, Gift } from "lucide-react";
import { toast } from "sonner";

/**
 * Super-admin override dialog: directly set a school's subscription plan
 * without going through the receipt-upload flow. For complimentary grants,
 * offline payments, or manual fixes.
 */
export default function AdminSetPlanDialog({ open, onClose, school, onChanged }) {
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [duration, setDuration] = useState(30);
  const [stack, setStack] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    if (!open) return;
    setNote(""); setStack(false);
    (async () => {
      try {
        const [p, d] = await Promise.all([
          api.get("/admin/subscription-plans"),
          school?.id ? api.get(`/schools/${school.id}/detail`) : Promise.resolve(null),
        ]);
        const active = (p.data?.items || []).filter((x) => x.is_active);
        setPlans(active);
        setCurrent(d?.data || null);
        // Default to current plan if exists, else first paid plan
        const curPlanId = d?.data?.subscription?.plan_id;
        setPlanId(curPlanId || (active.find((x) => !x.is_free) || active[0])?.id || "");
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [open, school?.id]);

  const selectedPlan = plans.find((p) => p.id === planId);
  const internalPlan = plans.find((p) => p.is_internal_full_access);
  const pickInternal = () => {
    if (!internalPlan) return;
    setPlanId(internalPlan.id);
    setDuration(internalPlan.duration_days || 9125);
    setStack(false);
    if (!note.trim()) setNote("Company-owned school — full access, no billing.");
  };
  useEffect(() => {
    if (selectedPlan) setDuration(selectedPlan.duration_days || 30);
  }, [selectedPlan?.id]);

  const submit = async () => {
    if (!planId) return toast.error("Pick a plan");
    setBusy(true);
    try {
      const isInternal = selectedPlan?.is_internal_full_access;
      const payload = {
        plan_id: planId,
        note: note.trim() || null,
        stack_on_existing: stack,
      };
      // Internal & Free plans: server decides the synthetic expiry — don't send.
      // Otherwise clamp to the backend's hard cap (3650 days = 10 years).
      if (!isInternal && !selectedPlan?.is_free) {
        const d = Math.min(Number(duration) || 0, 3650);
        if (d > 0) payload.duration_days = d;
      }
      const r = await api.post(`/admin/schools/${school.id}/set-plan`, payload);
      toast.success(`Plan set to "${r.data.plan}"`);
      onChanged?.(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const curName = current?.plan?.name || current?.subscription?.plan_name || "Free";
  const curExp = current?.subscription?.expires_at;
  const isSamePlan = selectedPlan && current?.subscription?.plan_id === selectedPlan.id;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="admin-set-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" /> Set plan for {school?.name}</DialogTitle>
          <DialogDescription>
            Direct override — no receipt required. Use for complimentary grants, offline payments, or manual fixes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-2xl p-3 text-sm">
            <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider mb-1">Current plan</div>
            <div className="font-bold">{curName}</div>
            {curExp && <div className="text-xs text-slate-500">Active until {new Date(curExp).toLocaleDateString()}</div>}
          </div>

          {internalPlan && (
            <button
              type="button"
              onClick={pickInternal}
              className={`w-full text-left rounded-2xl border p-3 flex items-start gap-3 transition ${
                planId === internalPlan.id
                  ? "border-amber-400 bg-amber-50 ring-2 ring-amber-200"
                  : "border-slate-200 hover:border-amber-300 hover:bg-amber-50/40"
              }`}
              data-testid="set-plan-internal-quick"
            >
              <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <div className="font-bold text-sm">Free full access (company-owned)</div>
                <div className="text-xs text-slate-600 mt-0.5">All {internalPlan.modules.length} modules, no expiry, no billing. Use only for schools you own.</div>
              </div>
            </button>
          )}

          <div>
            <Label>Or pick a specific plan</Label>
            <Select value={planId} onValueChange={setPlanId}>
              <SelectTrigger data-testid="set-plan-select"><SelectValue placeholder="Select plan" /></SelectTrigger>
              <SelectContent>
                {plans.map((p) => {
                  const tail = p.is_internal_full_access
                    ? "(Internal · Full Access)"
                    : p.is_free
                      ? "(Free)"
                      : `· ₱${(p.price_php || 0).toLocaleString()} / ${p.billing_cycle}`;
                  return (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} {tail}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          {selectedPlan && !selectedPlan.is_free && !selectedPlan.is_internal_full_access && (
            <>
              <div>
                <Label>Duration (days)</Label>
                <Input type="number" min="1" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="set-plan-duration" />
                <p className="text-xs text-slate-500 mt-1">Defaults to the plan's {selectedPlan.duration_days}-day cycle.</p>
              </div>
              {isSamePlan && (
                <label className="flex items-center justify-between gap-3 text-sm border border-slate-200 rounded-2xl px-3 py-2.5">
                  <span>Stack on current expiry (else reset from today)</span>
                  <Switch checked={stack} onCheckedChange={setStack} data-testid="set-plan-stack" />
                </label>
              )}
            </>
          )}
          <div>
            <Label>Note (optional — shown to owner)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder="e.g. Complimentary 30-day pilot." data-testid="set-plan-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !planId} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="set-plan-submit">
            <Sparkles className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : "Set plan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
